import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axios from "../../../../utils/api-client";
import { setSupervisorRateLimitError } from "../../../user/user-slice";
import {
	FetchShiftChangeRequestsParams,
	ShiftChangeActionParams,
	ShiftChangeImpact,
	ShiftChangeRequest,
	ShiftChangeRequestsState,
} from "../types";

const BASE_URL = `${process.env.REACT_APP_API_END_POINT}/supervisors/shift-change-requests`;

// Generic code used when a failure carries no API refusal code (network error,
// 500, unexpected body). Mapped to the generic error message in the UI.
export const UNKNOWN_ERROR_CODE = "Unknown";

const initialState: ShiftChangeRequestsState = {
	requests: [],
	getRequests: {
		status: "idle",
		errorMessage: "",
	},
	impact: {
		status: "idle",
		requestId: null,
		data: null,
	},
	action: {
		status: "idle",
		type: null,
		errorCode: null,
	},
};

const getErrorCode = (error: any): string =>
	typeof error?.response?.data?.code === "string"
		? error.response.data.code
		: UNKNOWN_ERROR_CODE;

export const fetchShiftChangeRequests = createAsyncThunk<
	ShiftChangeRequest[],
	FetchShiftChangeRequestsParams
>(
	"shift-change-requests/fetch",
	async (
		{ startDate, endDate, statusCodes },
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const queryParams = new URLSearchParams();
			if (statusCodes && statusCodes.length > 0) {
				queryParams.append("statusCodes", statusCodes.join(","));
			}
			if (startDate) queryParams.append("startDate", startDate);
			if (endDate) queryParams.append("endDate", endDate);
			const res = await axios.get(`${BASE_URL}?${queryParams.toString()}`);
			return fulfillWithValue(Array.isArray(res.data) ? res.data : []);
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue(getErrorCode(error));
		}
	}
);

export const fetchShiftChangeImpact = createAsyncThunk<
	ShiftChangeImpact,
	number
>(
	"shift-change-requests/impact",
	async (requestId, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(`${BASE_URL}/${requestId}/impact`);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(getErrorCode(error));
		}
	}
);

export const actOnShiftChangeRequest = createAsyncThunk<
	{ requestId: number },
	ShiftChangeActionParams
>(
	"shift-change-requests/act",
	async (
		{ requestId, type, comment },
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const trimmed = comment?.trim();
			const res = await axios.post(
				`${BASE_URL}/${requestId}/${type}`,
				trimmed ? { comment: trimmed } : {}
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(getErrorCode(error));
		}
	}
);

const shiftChangeRequestsSlice = createSlice({
	name: "shiftChangeRequests",
	initialState,
	reducers: {
		resetShiftChangeAction: (state) => {
			state.action = initialState.action;
		},
		resetShiftChangeImpact: (state) => {
			state.impact = initialState.impact;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchShiftChangeRequests.pending, (state) => {
				state.getRequests.status = "pending";
				state.getRequests.errorMessage = "";
			})
			.addCase(fetchShiftChangeRequests.fulfilled, (state, action) => {
				state.getRequests.status = "fulfilled";
				state.requests = action.payload;
			})
			.addCase(fetchShiftChangeRequests.rejected, (state, action) => {
				state.getRequests.status = "error";
				state.getRequests.errorMessage =
					(action.payload as string) ?? UNKNOWN_ERROR_CODE;
			})
			.addCase(fetchShiftChangeImpact.pending, (state, action) => {
				state.impact.status = "pending";
				state.impact.requestId = action.meta.arg;
				state.impact.data = null;
			})
			.addCase(fetchShiftChangeImpact.fulfilled, (state, action) => {
				// Ignore a late response for a dialog that has since changed.
				if (state.impact.requestId !== action.meta.arg) return;
				state.impact.status = "fulfilled";
				state.impact.data = action.payload;
			})
			.addCase(fetchShiftChangeImpact.rejected, (state, action) => {
				if (state.impact.requestId !== action.meta.arg) return;
				state.impact.status = "error";
				state.impact.data = null;
			})
			.addCase(actOnShiftChangeRequest.pending, (state, action) => {
				state.action.status = "pending";
				state.action.type = action.meta.arg.type;
				state.action.errorCode = null;
			})
			.addCase(actOnShiftChangeRequest.fulfilled, (state) => {
				state.action.status = "fulfilled";
			})
			.addCase(actOnShiftChangeRequest.rejected, (state, action) => {
				state.action.status = "error";
				state.action.errorCode =
					(action.payload as string) ?? UNKNOWN_ERROR_CODE;
			});
	},
});

export const { resetShiftChangeAction, resetShiftChangeImpact } =
	shiftChangeRequestsSlice.actions;

export default shiftChangeRequestsSlice.reducer;
